import { Component, computed, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SidebarModule } from 'primeng/sidebar';
import { ButtonModule } from 'primeng/button';
import { AccordionModule } from 'primeng/accordion';
import { TagModule } from 'primeng/tag';
import { DividerModule } from 'primeng/divider';
import { MessageModule } from 'primeng/message';
import { TabViewModule } from 'primeng/tabview';
import { GlobalsStore } from '../../../stores/globals.state';
import { BookmarkStore } from '../../../stores/bookmark/bookmark.state';
import { BookmarkValidationService } from '../../../services/bookmark-validation.service';
import {
  BookmarkValidationIssue,
  BookmarkValidationResult,
  GroupedIssue,
} from '../../../models/bookmark-validation';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { MessageService } from 'primeng/api';
import { DataLayer } from '../../../models/data-layer';
import { HeaderComponent } from '../../atoms/header/header.component';
import { TooltipModule } from 'primeng/tooltip';
import { MaskComponent } from '../mask/mask.component';
import { RoutingService } from '../../../services/routing.service';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { AlertComponent } from '../../atoms/alert/alert.component';
import { BadgeModule } from 'primeng/badge';
import { getState } from '@ngrx/signals';

@Component({
  selector: 'app-bookmark-validation-panel',
  standalone: true,
  imports: [
    CommonModule,
    SidebarModule,
    ButtonModule,
    AccordionModule,
    TagModule,
    DividerModule,
    MessageModule,
    TabViewModule,
    HeaderComponent,
    TooltipModule,
    MaskComponent,
    AlertComponent,
    BadgeModule,
  ],
  templateUrl: './bookmark-validation-panel.component.html',
  styleUrl: './bookmark-validation-panel.component.scss',
})
export class BookmarkValidationPanelComponent {
  readonly globalsStore = inject(GlobalsStore);
  readonly bookmarkStore = inject(BookmarkStore);
  readonly validationService = inject(BookmarkValidationService);
  readonly indicatorDataStore = inject(IndicatorDataStore);
  readonly dataLayerStore = inject(DataLayerStore);
  readonly indicatorStore = inject(IndicatorStore);
  readonly messageService = inject(MessageService);
  readonly routingService = inject(RoutingService);
  readonly activeUserStore = inject(ActiveUserStore);
  readonly confirmationDialogService = inject(ConfirmationDialogService);

  visible = false;

  validationSummary = computed(() => this.globalsStore.bookmarkValidation());

  invalidBookmarks = computed(() => {
    const summary = this.validationSummary();
    return summary?.results.filter(r => !r.isValid) || [];
  });

  invalidAccessBookmarks = computed(() => {
    const summary = this.validationSummary();
    return summary?.accessValidation?.results.filter(r => !r.isValid) || [];
  });

  invalidDataBookmarks = computed(() => {
    const summary = this.validationSummary();
    return summary?.dataValidation?.results.filter(r => !r.isValid) || [];
  });

  /**
   * Opens the validation panel
   */
  open(): void {
    this.visible = true;
    this.runValidation();
  }

  /**
   * Closes the validation panel
   */
  close(): void {
    this.visible = false;
  }

  /**
   * Runs validation on all bookmarks (both public and access validation)
   */
  runValidation(): void {
    const bookmarks = this.bookmarkStore.asArray();
    const summary =
      this.validationService.validateBookmarksWithAccess(bookmarks);
    this.globalsStore.setBookmarkValidation(summary);
  }

  /**
   * Gets CSS class for issue severity
   */
  getIssueSeverityClass(issue: BookmarkValidationIssue): string {
    switch (issue.issueType) {
      case 'unpublished-datalayer':
        return 'warning';
      case 'unpublished-data':
        return 'warning';
      case 'unpublished-indicator':
        return 'info';
      case 'missing-datalayer':
        return 'danger';
      case 'missing-indicator':
        return 'danger';
      default:
        return 'info';
    }
  }

  /**
   * Gets icon for issue type
   */
  getIssueIcon(issue: BookmarkValidationIssue): string {
    switch (issue.issueType) {
      case 'unpublished-datalayer':
        return 'fas fa-calendar';
      case 'unpublished-data':
        return 'fas fa-database';
      case 'unpublished-indicator':
        return 'fas fa-ruler-vertical';
      case 'missing-datalayer':
        return 'fas fa-calendar-xmark';
      case 'missing-indicator':
        return 'fas fa-triangle-exclamation';
      case 'workspace-restricted-datalayer':
        return 'fas fa-lock';
      case 'workspace-restricted-indicator':
        return 'fas fa-lock';
      case 'permission-denied-data':
        return 'fas fa-ban';
      default:
        return 'fas fa-exclamation-circle';
    }
  }

  /**
   * Gets icon for widget type
   */
  getWidgetTypeIcon(widgetType: string): string {
    const icons: { [key: string]: string } = {
      map: 'fas fa-map-location-dot',
      bar: 'fas fa-chart-column',
      line: 'fas fa-chart-line',
      pie: 'fas fa-chart-pie',
      scatter: 'fas fa-arrow-up-right-dots',
      bubble: 'fas fa-circle',
      radar: 'fas fa-satellite-dish',
      polarArea: 'fas fa-snowflake',
      table: 'fas fa-table',
      text: 'fas fa-font',
      info: 'fas fa-info-circle',
      teaser: 'fas fa-pager',
    };

    return icons[widgetType] || 'fas fa-chart-bar';
  }

  /**
   * Publishes a data layer
   */
  private async publishDataLayer(
    issue: BookmarkValidationIssue
  ): Promise<void> {
    if (!issue.details.dataLayerId) return;

    const dataLayer: DataLayer = this.dataLayerStore.getById(
      issue.details.dataLayerId
    );
    if (!dataLayer) return;

    try {
      await this.dataLayerStore.update({
        ...dataLayer,
        published: true,
        message: {
          severity: 'success',
          summary: 'Veröffentlicht',
          detail: `Jahr "${dataLayer.year}" wurde veröffentlicht.`,
        },
      });

      // Re-run validation after successful update
      setTimeout(() => this.runValidation(), 300);
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Fehler',
        detail: 'Jahr konnte nicht veröffentlicht werden.',
        life: 3000,
      });
    }
  }

  /**
   * Publishes indicator data
   */
  private async publishIndicatorData(
    issue: BookmarkValidationIssue
  ): Promise<void> {
    if (!issue.details.indicatorDataId) return;

    const indicatorData = this.indicatorDataStore.getById(
      issue.details.indicatorDataId
    );
    if (!indicatorData) return;

    try {
      await this.indicatorDataStore.update({
        ...indicatorData,
        published: true,
        message: {
          severity: 'success',
          summary: 'Veröffentlicht',
          detail: `Daten für "${issue.details.indicatorName}" wurden veröffentlicht.`,
        },
      });

      // Re-run validation after successful update
      setTimeout(() => this.runValidation(), 300);
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Fehler',
        detail: 'Daten konnten nicht veröffentlicht werden.',
        life: 3000,
      });
    }
  }

  /**
   * Fixes all issues in a bookmark (with strong confirmation warning)
   */
  async fixAllIssuesInBookmark(
    result: BookmarkValidationResult,
    event?: Event
  ): Promise<void> {
    // Only show confirmation for fixable issues
    const fixableIssues = result.issues.filter(
      issue =>
        issue.issueType === 'unpublished-datalayer' ||
        issue.issueType === 'unpublished-data'
    );

    if (fixableIssues.length === 0) {
      this.messageService.add({
        severity: 'info',
        summary: 'Keine behebbare Probleme',
        detail: 'Diese Ansicht enthält keine automatisch behebbare Probleme.',
        life: 3000,
      });
      return;
    }

    // Show strong warning confirmation
    this.confirmationDialogService.confirmValidationFixAll(
      event,
      result.bookmarkTitle,
      fixableIssues.length,
      async () => {
        try {
          // Fix all issues without individual confirmations
          for (const issue of fixableIssues) {
            if (issue.issueType === 'unpublished-datalayer') {
              await this.publishDataLayerSilent(issue);
            } else if (issue.issueType === 'unpublished-data') {
              await this.publishIndicatorDataSilent(issue);
            }
          }
          // Re-run validation after successful update
          setTimeout(() => this.runValidation(), 300);
        } catch (error) {
          this.messageService.add({
            severity: 'error',
            summary: 'Fehler',
            detail: 'Einige Probleme konnten nicht behoben werden.',
            life: 5000,
          });
        }
      }
    );
  }

  /**
   * Publishes a data layer silently (without success message)
   * Used for batch operations
   */
  private async publishDataLayerSilent(
    issue: BookmarkValidationIssue
  ): Promise<void> {
    if (!issue.details.dataLayerId) return;

    const dataLayer = this.dataLayerStore.getById(issue.details.dataLayerId);
    if (!dataLayer) return;

    await this.dataLayerStore.update({
      ...dataLayer,
      published: true,
    });
  }

  /**
   * Publishes indicator data silently (without success message)
   * Used for batch operations
   */
  private async publishIndicatorDataSilent(
    issue: BookmarkValidationIssue
  ): Promise<void> {
    if (!issue.details.indicatorDataId) return;

    const indicatorData = this.indicatorDataStore.getById(
      issue.details.indicatorDataId
    );
    if (!indicatorData) return;

    await this.indicatorDataStore.update({
      ...indicatorData,
      published: true,
    });
  }

  /**
   * Gets the public bookmark link URL
   */
  getBookmarkLink(bookmarkId: string, published = true): string {
    const commune = this.activeUserStore.commune();
    return this.routingService.getAbsoluteBookmarkLink(
      bookmarkId,
      published,
      commune
    );
  }

  /**
   * Opens the bookmark in a new tab (public view)
   */
  openBookmarkInPublicView(bookmarkId: string): void {
    const link = this.getBookmarkLink(bookmarkId);
    window.open(link, '_blank');
  }

  /**
   * Opens the bookmark in a new tab (public view)
   */
  openBookmarkInInternalView(bookmarkId: string): void {
    const link = this.getBookmarkLink(bookmarkId, false);
    window.open(link, '_blank');
  }

  /**
   * Copies the public bookmark link to clipboard
   */
  async copyBookmarkLink(bookmarkId: string): Promise<void> {
    const link = this.getBookmarkLink(bookmarkId);
    try {
      await navigator.clipboard.writeText(link);
      this.messageService.add({
        severity: 'success',
        summary: 'Link kopiert',
        detail: 'Der Link wurde in die Zwischenablage kopiert.',
        life: 3000,
      });
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Fehler',
        detail: 'Link konnte nicht kopiert werden.',
        life: 3000,
      });
    }
  }

  /**
   * Groups issues by their unique identifier
   * - unpublished-datalayer → dataLayerId
   * - unpublished-data → indicatorId + dataLayerId (combination)
   * - missing-datalayer → dataLayerId
   * - missing-indicator → indicatorId
   * - workspace-restricted-datalayer → dataLayerId
   * - workspace-restricted-indicator → indicatorId
   */
  groupIssues(issues: BookmarkValidationIssue[]): GroupedIssue[] {
    const groupMap = new Map<string, GroupedIssue>();

    issues.forEach(issue => {
      let groupKey: string;
      let displayName: string;

      // Determine grouping key based on issue type
      switch (issue.issueType) {
        case 'unpublished-datalayer':
        case 'missing-datalayer':
        case 'workspace-restricted-datalayer':
          groupKey = `${issue.issueType}:${issue.details.dataLayerId}`;
          displayName = issue.details.dataLayerName || 'Unbekanntes Jahr';
          break;
        case 'unpublished-data':
          // Group by BOTH indicator._id AND dataLayer._id
          groupKey = `${issue.issueType}:${issue.details.indicatorId}:${issue.details.dataLayerId}`;
          displayName = `${issue.details.indicatorName || 'Unbekannter Indikator'} (${issue.details.dataLayerName || 'Jahr'})`;
          break;
        case 'missing-indicator':
        case 'unpublished-indicator':
        case 'workspace-restricted-indicator':
          groupKey = `${issue.issueType}:${issue.details.indicatorId}`;
          displayName = issue.details.indicatorName || 'Unbekannter Indikator';
          break;
        default:
          groupKey = `${issue.issueType}:${issue.widgetId}`;
          displayName = 'Unbekanntes Problem';
      }

      // Get or create group
      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, {
          groupKey,
          issueType: issue.issueType,
          issues: [],
          displayName,
          details: { ...issue.details },
          message: issue.message,
        });
      }

      // Add issue to group
      groupMap.get(groupKey)!.issues.push(issue);
    });

    return Array.from(groupMap.values());
  }

  /**
   * Fixes all issues in a group (same underlying problem, multiple widgets)
   */
  async fixGroupedIssue(group: GroupedIssue, event?: Event): Promise<void> {
    // All issues in the group have the same fix, so we just use the first one
    const firstIssue = group.issues[0];

    switch (group.issueType) {
      case 'unpublished-datalayer':
        // Use the existing DataLayer publication confirmation
        // eslint-disable-next-line no-case-declarations
        const dataLayer = this.dataLayerStore.getById(
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          firstIssue.details.dataLayerId!
        );
        if (dataLayer) {
          this.dataLayerStore.singleSelection(dataLayer._id);
          setTimeout(() => {
            this.confirmationDialogService.confirmDataLayerPublication(
              event,
              dataLayer,
              () => this.publishDataLayer(firstIssue)
            );
          }, 300);
        }
        break;
      case 'unpublished-data':
        // Use the existing publishData confirmation
        this.indicatorStore.singleSelection(
          firstIssue.details.indicatorId || ''
        );
        this.dataLayerStore.singleSelection(
          firstIssue.details.dataLayerId || ''
        );
        setTimeout(() => {
          this.confirmationDialogService.confirmPublishData(
            () => this.publishIndicatorData(firstIssue),
            `Daten für "${firstIssue.details.indicatorName}" veröffentlichen`,
            'publishDialog'
          );
        }, 300);
        break;
      case 'unpublished-indicator':
        // Indicators don't have a published flag in the current model
        this.messageService.add({
          severity: 'info',
          summary: 'Nicht unterstützt',
          detail:
            'Indikatoren können derzeit nicht automatisch veröffentlicht werden.',
          life: 3000,
        });
        break;
      case 'missing-datalayer':
        this.confirmationDialogService.confirmRemoveMissingDataLayer(
          event,
          group.displayName,
          group.issues.length,
          () => this.removeMissingDataLayerReferences(group)
        );
        break;
      case 'missing-indicator':
        this.confirmationDialogService.confirmRemoveMissingIndicator(
          event,
          group.displayName,
          group.issues.length,
          () => this.removeMissingIndicatorReferences(group)
        );
        break;
    }
  }

  /**
   * Removes missing data layer references from widget configurations
   */
  private async removeMissingDataLayerReferences(
    group: GroupedIssue
  ): Promise<void> {
    const missingDataLayerId = group.details.dataLayerId;
    if (!missingDataLayerId) return;

    // Get unique bookmark IDs
    const bookmarkIds = [
      ...new Set(group.issues.map(issue => issue.bookmarkId)),
    ];

    for (const bookmarkId of bookmarkIds) {
      const bookmark = this.bookmarkStore.getById(bookmarkId);
      if (!bookmark) continue;

      // Clone the config array
      const updatedConfig = bookmark.config.map(widget => {
        // Check if this widget is affected
        const affectedIssue = group.issues.find(
          issue => issue.widgetId === widget.id
        );
        if (!affectedIssue) return widget;

        // Clone widget and its config
        const updatedWidget = { ...widget };
        updatedWidget.widget = { ...widget.widget };

        // Remove the missing dataLayer reference
        if (updatedWidget.widget.dataLayer === missingDataLayerId) {
          updatedWidget.widget.dataLayer = '';
        }

        // Remove from dataLayers array if present
        if (updatedWidget.widget.dataLayers) {
          updatedWidget.widget.dataLayers =
            updatedWidget.widget.dataLayers.filter(
              id => id !== missingDataLayerId
            );
        }

        return updatedWidget;
      });

      // Update the bookmark
      try {
        await this.bookmarkStore.update({
          ...bookmark,
          config: updatedConfig,
          message: {
            severity: 'success',
            summary: 'Referenz entfernt',
            detail: `Fehlende Jahr-Referenz wurde aus "${bookmark.title}" entfernt.`,
          },
        });
      } catch (error) {
        this.messageService.add({
          severity: 'error',
          summary: 'Fehler',
          detail: `Ansicht "${bookmark.title}" konnte nicht aktualisiert werden.`,
          life: 3000,
        });
      }
    }
    // Re-run validation after successful update
    setTimeout(() => this.runValidation(), 300);
  }

  /**
   * Removes missing indicator references from widget configurations
   */
  private async removeMissingIndicatorReferences(
    group: GroupedIssue
  ): Promise<void> {
    const missingIndicatorId = group.details.indicatorId;

    if (!missingIndicatorId) return;

    // Get unique bookmark IDs
    const bookmarkIds = [
      ...new Set(group.issues.map(issue => issue.bookmarkId)),
    ];

    for (const bookmarkId of bookmarkIds) {
      const bookmark = this.bookmarkStore.getById(bookmarkId);

      if (!bookmark) continue;

      // Clone the config array
      const updatedConfig = bookmark.config.map(widget => {
        // Check if this widget is affected
        const affectedIssue = group.issues.find(
          issue => String(issue.widgetId) === String(widget.id)
        );

        if (!affectedIssue) return widget;

        // Clone widget and its config
        const updatedWidget = { ...widget };
        updatedWidget.widget = { ...widget.widget };

        // Remove the missing indicator reference
        if (updatedWidget.widget.indicator === missingIndicatorId) {
          updatedWidget.widget.indicator = '';
        }

        // Remove from indicators array if present
        if (updatedWidget.widget.indicators) {
          updatedWidget.widget.indicators =
            updatedWidget.widget.indicators.filter(
              id => id !== missingIndicatorId
            );
        }

        // Remove from axis array if present
        if (updatedWidget.widget.axisProperties) {
          updatedWidget.widget.axisProperties =
            updatedWidget.widget.axisProperties.filter(
              id => id !== missingIndicatorId
            );
        }

        return updatedWidget;
      });

      // Update the bookmark
      try {
        await this.bookmarkStore.update({
          ...bookmark,
          config: updatedConfig,
          message: {
            severity: 'success',
            summary: 'Referenz entfernt',
            detail: `Fehlende Indikator-Referenz wurde aus "${bookmark.title}" entfernt.`,
          },
        });
      } catch (error) {
        this.messageService.add({
          severity: 'error',
          summary: 'Fehler',
          detail: `Ansicht "${bookmark.title}" konnte nicht aktualisiert werden.`,
          life: 3000,
        });
      }
    }
    // Re-run validation after successful update
    setTimeout(() => this.runValidation(), 300);
  }

  /**
   * Gets action suggestion text for access issues
   */
  getAccessIssueSuggestion(group: GroupedIssue): string {
    switch (group.issueType) {
      case 'workspace-restricted-datalayer':
        return `Kontaktieren Sie einen Administrator, um Zugriff auf das Jahr "${group.displayName}" zu erhalten. Das Jahr ist in einem Arbeitsbereich, auf den Sie keinen Zugriff haben.`;
      case 'workspace-restricted-indicator':
        return `Kontaktieren Sie einen Administrator, um Zugriff auf den Indikator "${group.displayName}" zu erhalten. Der Indikator ist in einem Arbeitsbereich, auf den Sie keinen Zugriff haben.`;
      case 'permission-denied-data':
        return `Sie benötigen erweiterte Berechtigungen, um auf diese Daten zuzugreifen. Kontaktieren Sie einen Administrator.`;
      default:
        return 'Kontaktieren Sie einen Administrator für weitere Informationen.';
    }
  }
}
